import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { ADD_PRODUCTS_ROUTE, ADD_PRODUCTS_SEARCH_ROUTE, ServiceStateEnum } from '@mwe/constants';
import { AddressProducts } from '@mwe/models';
import {
  AccountLogic,
  DsgvoLogic,
  NavigationLogic,
  PopupService,
  ProductAddStateService,
  ProductLogic,
  ProductService,
  ProductStateService,
} from '@mwe/services';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
// eslint-disable-next-line
import { InlineNotificationComponent } from '../../../../../ui/components/src/lib/inline-notification/inline-notification.component';
import { HeadlineService, ProductsGroupComponent } from '@mwe/ui/components';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'mwe-product-choose-base',
  templateUrl: './product-choose-base.component.html',
  providers: [HeadlineService],
  imports: [ProductsGroupComponent, InlineNotificationComponent],
})
export class ProductChooseBaseComponent implements OnInit {
  state = ServiceStateEnum.INIT;
  alertTitleKey: string;
  alertMessageKey: string;
  addressGroups: AddressProducts[];

  private productAddStateService = inject(ProductAddStateService);
  private productService = inject(ProductService);
  private productLogic = inject(ProductLogic);
  private router = inject(Router);
  private productStateService = inject(ProductStateService);
  private popupService = inject(PopupService);
  private navigationLogic = inject(NavigationLogic);
  private translateService = inject(TranslateService);
  private accountLogic = inject(AccountLogic);
  private dsgvoLogic = inject(DsgvoLogic);
  private headlineService = inject(HeadlineService);
  private destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    this.headlineService.setReferenceHeadline('h1');
    this.addressGroups = Array.from(this.productAddStateService.addressGroups.values());
  }

  async submit(): Promise<void> {
    await this.addProducts();
    await this.showSearchConfirmationPopup();
    throw new Error('prevent wizard.nextStep()');
  }

  async addProducts(): Promise<void> {
    try {
      this.state = ServiceStateEnum.LOADING;
      await this.productService.addProducts(this.productAddStateService.businessPartnerNumber, this.productAddStateService.systemId);

      if (!this.accountLogic.isImpersonate()) {
        await this.dsgvoLogic.triggerDsgvoForAssociations(
          this.productAddStateService.businessPartnerNumber,
          this.productAddStateService.systemId,
        );
      }

      this.productAddStateService.associationCompleted = true;
      this.productStateService.clearProducts();
      this.productLogic.clearAssociationsUpdate();
      this.productLogic.refreshProducts().subscribe();
      this.state = ServiceStateEnum.SUCCESS;
    } catch (error) {
      this.alertTitleKey = 'products.error.title';
      this.alertMessageKey = 'products.error.description';
      this.state = ServiceStateEnum.ERROR;
      throw error;
    }
  }

  async showSearchConfirmationPopup(): Promise<void> {
    const label = this.getNavigationBackLabel();
    this.popupService
      .showAddAssociationConfirmationPopup(true, label)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(result => {
        if (result !== 'product-choose-confirmation' && result !== 'suggestions-popup-closed') {
          this.productAddStateService.clearData();
          this.router.navigateByUrl(`/${ADD_PRODUCTS_ROUTE}/${ADD_PRODUCTS_SEARCH_ROUTE}`);
        } else {
          this.router.navigateByUrl(this.navigationLogic.getNavigateBackRouteAndRemoveFromState());
        }
      });
  }

  private getNavigationBackLabel(): string {
    const processKey = this.navigationLogic.getProcessLabelKeyFromLatestNavigatingBackItem();

    if (!processKey) {
      return 'products.confirm.popup.buttonOk';
    }

    const process = this.translateService.instant(processKey);
    return this.translateService.instant('products-add.product-choose.popup.confirm', { process });
  }
}
